import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authApi, contactApi } from '../../api/client';
import ReCAPTCHA from 'react-google-recaptcha';
import styles from './Contact.module.css';

const TOPICS = [
  { value: 'inquiry', label: 'Inquiry' },
  { value: 'feature_request', label: 'Feature request' },
  { value: 'bug_report', label: 'Bug report' },
  { value: 'general', label: 'General' },
] as const;

export function Contact() {
  const { isAuthenticated } = useAuth();
  const [topic, setTopic] = useState<string>(TOPICS[0].value);
  const [email, setEmail] = useState('');
  const [details, setDetails] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const recaptchaRef = useRef<any>(null);

  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    authApi
      .me()
      .then((me) => {
        if (!cancelled && me.email) setEmail(me.email);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (recaptchaSiteKey && !recaptchaToken) {
      setError('Please complete the CAPTCHA.');
      return;
    }
    setLoading(true);
    try {
      await contactApi.submit({
        topic,
        email: email.trim(),
        details: details.trim(),
        ...(recaptchaSiteKey && recaptchaToken && { recaptchaToken }),
      });
      setSuccess('Thanks — your message was sent.');
      setDetails('');
      setRecaptchaToken(null);
      recaptchaRef.current?.reset?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      setRecaptchaToken(null);
      recaptchaRef.current?.reset?.();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <h1>Contact</h1>
        <p className={styles.subtitle}>
          Send a message to the RideSpot team. We read every submission.
        </p>

        {success && <p className={styles.bannerSuccess} role="status">{success}</p>}
        {error && <p className={styles.bannerError} role="alert">{error}</p>}

        <form onSubmit={handleSubmit} className={styles.form}>
          <label className={styles.label}>
            Topic
            <select
              className={styles.select}
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              required
            >
              {TOPICS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>

          <label className={styles.label}>
            Your email
            <input
              type="email"
              className={styles.input}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="you@example.com"
            />
          </label>

          <label className={styles.label}>
            Details
            <textarea
              className={styles.textarea}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              required
              minLength={5}
              maxLength={10000}
              rows={8}
              placeholder="How can we help?"
            />
          </label>

          {recaptchaSiteKey && (
            <div className={styles.captchaRow}>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={recaptchaSiteKey}
                onChange={(token: string | null) => {
                  setRecaptchaToken(token);
                  if (token && error === 'Please complete the CAPTCHA.') setError(null);
                }}
                onExpired={() => setRecaptchaToken(null)}
              />
            </div>
          )}

          <button type="submit" className={styles.submit} disabled={loading}>
            {loading ? 'Sending…' : 'Send message'}
          </button>
        </form>
      </div>
    </div>
  );
}
